/**
 * Helper function to generate uiSchema that disables MUI floating labels
 * and ensures labels appear above inputs as standard labels
 */

/**
 * Recursively builds uiSchema to disable floating labels for all fields
 * @param {Object} schema - JSON Schema object
 * @param {Object} existingUiSchema - Existing uiSchema to merge with
 * @returns {Object} - Complete uiSchema with label configurations
 */
export function buildUiSchemaWithTopLabels(schema, existingUiSchema = {}) {
  const uiSchema = { ...existingUiSchema };

  // Process properties
  if (schema.properties) {
    Object.keys(schema.properties).forEach((key) => {
      const property = schema.properties[key];

      // Initialize uiSchema for this field if not exists
      if (!uiSchema[key]) {
        uiSchema[key] = {};
      }

      // Skip if widget is already set to hidden or custom
      if (uiSchema[key]["ui:widget"] === "hidden") {
        return;
      }

      // For text/string/number fields, configure to show label above
      if (!property.properties && property.type !== "object" && property.type !== "array") {
        uiSchema[key] = {
          ...uiSchema[key],
          "ui:options": {
            ...uiSchema[key]["ui:options"],
            // Keep label rendering as title above field
            label: true,
            // Disable MUI's floating behavior by using standard variant
            InputLabelProps: {
              shrink: true, // Always shrink (position above)
            },
            // Use standard variant instead of outlined
            variant: "standard",
          },
        };
      }

      // Recursively handle nested objects
      if (property.type === "object" && property.properties) {
        uiSchema[key] = buildUiSchemaWithTopLabels(property, uiSchema[key] || {});
      }

      // Handle arrays of objects
      if (property.type === "array" && property.items?.properties) {
        if (!uiSchema[key]) {
          uiSchema[key] = {};
        }
        if (!uiSchema[key].items) {
          uiSchema[key].items = {};
        }
        uiSchema[key].items = buildUiSchemaWithTopLabels(property.items, uiSchema[key].items || {});
      }
    });
  }

  // Handle oneOf/anyOf schemas
  if (schema.oneOf || schema.anyOf) {
    const schemaList = schema.oneOf || schema.anyOf;
    schemaList.forEach((subSchema, index) => {
      if (subSchema.properties) {
        Object.keys(subSchema.properties).forEach((key) => {
          if (!uiSchema[key]) {
            uiSchema[key] = {};
          }
          const property = subSchema.properties[key];
          if (!property.properties && property.type !== "object" && property.type !== "array") {
            uiSchema[key] = {
              ...uiSchema[key],
              "ui:options": {
                ...uiSchema[key]["ui:options"],
                label: true,
                InputLabelProps: {
                  shrink: true,
                },
                variant: "standard",
              },
            };
          }
        });
      }
    });
  }

  // Handle dependencies
  if (schema.dependencies) {
    Object.keys(schema.dependencies).forEach((depKey) => {
      const dep = schema.dependencies[depKey];
      if (dep.oneOf) {
        dep.oneOf.forEach((branch) => {
          if (branch.properties) {
            Object.keys(branch.properties).forEach((key) => {
              if (!uiSchema[key]) {
                uiSchema[key] = {};
              }
              const property = branch.properties[key];
              if (!property.properties && property.type !== "object" && property.type !== "array") {
                uiSchema[key] = {
                  ...uiSchema[key],
                  "ui:options": {
                    ...uiSchema[key]["ui:options"],
                    label: true,
                    InputLabelProps: {
                      shrink: true,
                    },
                    variant: "standard",
                  },
                };
              }
            });
          }
        });
      }
    });
  }

  return uiSchema;
}

/**
 * Alternative: Use outlined variant but force label to always be shrunk
 * This keeps the outlined look but with label always positioned above
 */
export function buildUiSchemaWithShrunkLabels(schema, existingUiSchema = {}) {
  const uiSchema = { ...existingUiSchema };

  if (schema.properties) {
    Object.keys(schema.properties).forEach((key) => {
      const property = schema.properties[key];

      if (!uiSchema[key]) {
        uiSchema[key] = {};
      }

      if (uiSchema[key]["ui:widget"] === "hidden") {
        return;
      }

      if (!property.properties && property.type !== "object" && property.type !== "array") {
        uiSchema[key] = {
          ...uiSchema[key],
          "ui:options": {
            ...uiSchema[key]["ui:options"],
            InputLabelProps: {
              shrink: true,
            },
          },
        };
      }

      if (property.type === "object" && property.properties) {
        uiSchema[key] = buildUiSchemaWithShrunkLabels(property, uiSchema[key] || {});
      }

      if (property.type === "array" && property.items?.properties) {
        if (!uiSchema[key].items) {
          uiSchema[key].items = {};
        }
        uiSchema[key].items = buildUiSchemaWithShrunkLabels(property.items, uiSchema[key].items || {});
      }
    });
  }

  if (schema.dependencies) {
    Object.keys(schema.dependencies).forEach((depKey) => {
      const dep = schema.dependencies[depKey];
      if (dep.oneOf) {
        dep.oneOf.forEach((branch) => {
          if (branch.properties) {
            Object.keys(branch.properties).forEach((key) => {
              if (!uiSchema[key]) {
                uiSchema[key] = {};
              }
              const property = branch.properties[key];
              if (!property.properties && property.type !== "object" && property.type !== "array") {
                uiSchema[key] = {
                  ...uiSchema[key],
                  "ui:options": {
                    ...uiSchema[key]["ui:options"],
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                };
              }
            });
          }
        });
      }
    });
  }

  return uiSchema;
}
